import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, BookOpen, ExternalLink } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";

// Color mapping from Procedures page
const CODE_TYPE_COLORS: Record<string, string> = {
  'CPT': '#60a5fa',
  'DRG': '#c084fc',
  'HCPCS': '#4ade80',
  'REV': '#facc15',
  'NDC': '#fb7185',
  'SUPPLY': '#fb923c',
};

interface GuideItem {
  code: string;
  code_type: string;
  name: string;
  description?: string;
  llm_quick_name?: string;
  llm_summary_description?: string;
  has_guide: boolean;
  html_content?: string;
  created_at?: string;
}

export function RecentGuidesSwiper() {
  // Filter state
  const [selectedFilter, setSelectedFilter] = useState<string>('ALL');

  // Setup Embla Carousel with autoplay
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: 'start',
      slidesToScroll: 1,
      skipSnaps: false,
    },
    [Autoplay({ delay: 10000, stopOnInteraction: false })]
  );

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  // Update scroll button states
  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi, onSelect]);

  // Reset carousel when filter changes
  useEffect(() => {
    if (emblaApi) {
      emblaApi.reInit();
      emblaApi.scrollTo(0);
    }
  }, [selectedFilter, emblaApi]);

  // Navigation functions
  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  // Fetch recent guides from all code tables
  const { data: guides = [], isLoading } = useQuery({
    queryKey: ['recent-educational-guides'],
    queryFn: async () => {
      const allGuides: GuideItem[] = [];

      // Fetch from procedures (CPT, HCPCS, REV) - fetch each code type separately to ensure balanced representation
      const procedureTypes = ['CPT', 'HCPCS', 'REV'];
      const procedures: any[] = [];

      for (const codeType of procedureTypes) {
        const { data } = await supabase
          .from('procedures')
          .select('code, code_type, description, llm_quick_name, llm_summary_description, procedure_details_html, created_at')
          .eq('code_type', codeType)
          .not('procedure_details_html', 'is', null)
          .order('created_at', { ascending: false })
          .limit(50);

        if (data) {
          procedures.push(...data);
        }
      }

      if (procedures) {
        procedures.forEach(p => {
          // Skip potentially copyrighted descriptions for CPT and HCPCS codes (HCPCS Level I = CPT)
          const isProtectedCode = p.code_type === 'CPT' || p.code_type === 'HCPCS';

          allGuides.push({
            code: p.code,
            code_type: p.code_type,
            // Only use independently authored LLM content for CPT/HCPCS to avoid AMA copyright
            name: isProtectedCode ? '' : (p.description || ''),
            description: p.llm_summary_description,
            llm_quick_name: p.llm_quick_name,
            has_guide: !!p.procedure_details_html,
            html_content: p.procedure_details_html,
            created_at: p.created_at,
          });
        });
      }

      // Fetch from DRG codes
      const { data: drgCodes } = await supabase
        .from('drg_codes')
        .select('drg_code, description, llm_quick_name, llm_summary_description, procedure_details_html, created_at')
        .not('procedure_details_html', 'is', null)
        .order('created_at', { ascending: false })
        .limit(100);

      if (drgCodes) {
        drgCodes.forEach(d => {
          allGuides.push({
            code: d.drg_code,
            code_type: 'DRG',
            name: d.description || '',
            description: d.llm_summary_description,
            llm_quick_name: d.llm_quick_name,
            has_guide: !!d.procedure_details_html,
            html_content: d.procedure_details_html,
            created_at: d.created_at,
          });
        });
      }

      // Fetch from Drug codes
      const { data: drugCodes } = await supabase
        .from('drug_codes')
        .select('drug_code, drug_name, llm_quick_name, llm_summary_description, procedure_details_html, created_at')
        .not('procedure_details_html', 'is', null)
        .order('created_at', { ascending: false })
        .limit(100);

      if (drugCodes) {
        drugCodes.forEach(d => {
          allGuides.push({
            code: d.drug_code,
            code_type: 'NDC',
            name: d.drug_name || '',
            description: d.llm_summary_description,
            llm_quick_name: d.llm_quick_name,
            has_guide: !!d.procedure_details_html,
            html_content: d.procedure_details_html,
            created_at: d.created_at,
          });
        });
      }

      // Fetch from Supply codes
      const { data: supplyCodes } = await supabase
        .from('supply_codes')
        .select('supply_code, supply_description, llm_quick_name, llm_summary_description, procedure_details_html, created_at')
        .not('procedure_details_html', 'is', null)
        .order('created_at', { ascending: false })
        .limit(100);

      if (supplyCodes) {
        supplyCodes.forEach(s => {
          allGuides.push({
            code: s.supply_code,
            code_type: 'SUPPLY',
            name: s.supply_description || '',
            description: s.llm_summary_description,
            llm_quick_name: s.llm_quick_name,
            has_guide: !!s.procedure_details_html,
            html_content: s.procedure_details_html,
            created_at: s.created_at,
          });
        });
      }

      // Organize guides by code type for balanced distribution
      const guidesByType: Record<string, GuideItem[]> = {
        CPT: [],
        DRG: [],
        HCPCS: [],
        REV: [],
        NDC: [],
        SUPPLY: []
      };

      // Sort guides by created_at within each type
      allGuides.forEach(guide => {
        const type = guide.code_type.toUpperCase();
        if (guidesByType[type]) {
          guidesByType[type].push(guide);
        }
      });

      // Sort each type by most recent
      Object.keys(guidesByType).forEach(type => {
        guidesByType[type].sort((a, b) => {
          if (!a.created_at) return 1;
          if (!b.created_at) return -1;
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });
      });

      // Alternate between code types to create balanced distribution
      const balancedGuides: GuideItem[] = [];
      const typeOrder = ['CPT', 'DRG', 'HCPCS', 'REV', 'NDC', 'SUPPLY'];
      let index = 0;

      // Keep alternating through types until we have 30 guides or run out
      while (balancedGuides.length < 30) {
        let addedAny = false;

        for (const type of typeOrder) {
          if (guidesByType[type].length > index) {
            balancedGuides.push(guidesByType[type][index]);
            addedAny = true;

            if (balancedGuides.length >= 30) break;
          }
        }

        if (!addedAny) break; // No more guides to add
        index++;
      }

      return balancedGuides;
    },
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
  });

  const getCodeTypeUrl = (codeType: string, code: string) => {
    const lowerType = codeType.toLowerCase();
    if (lowerType === 'cpt') {
      return `/cpt/${code}`;
    } else if (lowerType === 'hcpcs') {
      return `/hcpcs/${code}`;
    } else if (lowerType === 'rev') {
      return `/rev/${code}`;
    } else if (lowerType === 'drg') {
      return `/drg/${code}`;
    } else if (lowerType === 'ndc') {
      return `/drugs/${code}`;
    } else if (lowerType === 'supply') {
      return `/supply/${code}`;
    }
    return `/cpt`;
  };

  if (isLoading) {
    return (
      <section className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 bg-secondary/30 z-10">
        <div style={{ marginLeft: '0px', marginRight: '0px' }}>
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-matrix-green mb-3 sm:mb-4">
              Recent Educational Guides
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-muted-foreground">
              Loading latest medical code guides...
            </p>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-6">
            {[...Array(5)].map((_, i) => (
              <Card key={i} className="bg-muted/20 animate-pulse">
                <CardContent className="p-6">
                  <div className="h-8 bg-muted rounded mb-4"></div>
                  <div className="h-4 bg-muted rounded mb-2"></div>
                  <div className="h-3 bg-muted rounded w-2/3"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (guides.length === 0) {
    return null;
  }

  // Filter guides based on selected filter
  const filteredGuides = selectedFilter === 'ALL'
    ? guides
    : guides.filter(guide => guide.code_type.toUpperCase() === selectedFilter);

  // Define filter buttons with colors matching Real-Time Analytics
  const filterButtons = [
    { label: 'All', value: 'ALL', color: '#9ca3af' },
    { label: 'CPT', value: 'CPT', color: '#60a5fa' },
    { label: 'DRG', value: 'DRG', color: '#c084fc' },
    { label: 'HCPCS', value: 'HCPCS', color: '#4ade80' },
    { label: 'REV', value: 'REV', color: '#facc15' },
    { label: 'NDC', value: 'NDC', color: '#fb7185' },
    { label: 'SUPPLY', value: 'SUPPLY', color: '#fb923c' },
  ];

  return (
    <section className="relative py-12 sm:py-16 lg:py-20 bg-secondary/30 z-10 flex justify-center" style={{ paddingLeft: '100px', paddingRight: '100px' }}>
      <div style={{ width: 'calc(100vw - 200px)', maxWidth: '100%' }}>
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-matrix-green mb-3 sm:mb-4 glitch" data-text="Recent Educational Guides">
            Recent Educational Guides
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-muted-foreground mb-6">
            Educational content for common medical codes
          </p>

          {/* Filter Buttons */}
          <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
            {filterButtons.map((filter) => (
              <button
                key={filter.value}
                onClick={() => setSelectedFilter(filter.value)}
                className="px-4 py-2 rounded font-mono text-sm font-bold transition-all hover:opacity-80"
                style={{
                  backgroundColor: selectedFilter === filter.value ? filter.color : '#000000a1',
                  color: selectedFilter === filter.value ? '#000000' : filter.color,
                  borderColor: filter.color,
                  borderWidth: '2px',
                  borderStyle: 'solid',
                  boxShadow: selectedFilter === filter.value ? `0 0 12px ${filter.color}40` : 'none'
                }}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {/* Embla Carousel */}
        {filteredGuides.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground text-lg">
              No guides found for {filterButtons.find(f => f.value === selectedFilter)?.label} codes yet.
            </p>
            <Button
              variant="outline"
              onClick={() => setSelectedFilter('ALL')}
              className="mt-4 border-matrix-green/30 text-matrix-green hover:bg-matrix-green/10"
            >
              Show All Guides
            </Button>
          </div>
        ) : (
          <div className="relative">
            <div className="overflow-hidden cursor-grab active:cursor-grabbing" ref={emblaRef}>
              <div className="flex gap-6 mb-6 select-none">
                {filteredGuides.map((guide, index) => {
                const color = CODE_TYPE_COLORS[guide.code_type.toUpperCase()] || '#9ca3af';
                const guideUrl = getCodeTypeUrl(guide.code_type, guide.code);

              return (
                <div
                  key={`${guide.code_type}-${guide.code}-${index}`}
                  className="flex-shrink-0"
                  style={{
                    width: 'calc((100vw - 272px) / 4)', // 100vw - 200px (left+right padding) - 72px (3 card gaps) = 272px total overhead, divided by 4 cards
                    minWidth: '280px',
                    maxWidth: '500px'
                  }}
                >
                  <Card className="bg-black/60 border-matrix-green/30 hover:border-matrix-green/50 transition-all hover:shadow-lg hover:shadow-matrix-green/20 overflow-hidden flex flex-col"
                    style={{ height: '850px' }}
                  >
                  {/* Header */}
                  <Link to={guideUrl}>
                    <div className="p-4 pb-3 border-b border-matrix-green/20 cursor-pointer hover:bg-matrix-green/5 transition-colors">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <BookOpen className="w-5 h-5" style={{ color }} />
                          <Badge variant="outline" style={{ color, borderColor: `${color}40` }}>
                            {guide.code_type.toUpperCase()}
                          </Badge>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                          New
                        </Badge>
                      </div>

                      {/* Code */}
                      <div className="text-xl font-bold font-mono mb-1" style={{ color }}>
                        {guide.code}
                      </div>

                      {/* Procedure Name */}
                      {guide.name && (
                        <div className="text-sm font-semibold text-white line-clamp-1 mb-1">
                          {guide.name}
                        </div>
                      )}

                      {/* Consumer-Friendly Name in quotes */}
                      {guide.llm_quick_name && (
                        <div className="text-xs text-foreground line-clamp-1 mb-0.5">
                          "{guide.llm_quick_name}"
                        </div>
                      )}

                      {/* Description in parentheses */}
                      {guide.description && (
                        <div className="text-xs text-muted-foreground line-clamp-2">
                          ({guide.description})
                        </div>
                      )}
                    </div>
                  </Link>

                  {/* HTML Preview - Flexible height within 600px max */}
                  {guide.html_content && (
                    <div className="relative w-full flex-1 min-h-0 overflow-hidden">
                      <iframe
                        srcDoc={`
                          <style>
                            /* Custom scrollbar styling */
                            ::-webkit-scrollbar {
                              width: 8px;
                              height: 8px;
                            }
                            ::-webkit-scrollbar-track {
                              background: #1a1a1a;
                            }
                            ::-webkit-scrollbar-thumb {
                              background: #22c55e;
                              border-radius: 4px;
                            }
                            ::-webkit-scrollbar-thumb:hover {
                              background: #16a34a;
                            }
                            /* Firefox scrollbar styling */
                            * {
                              scrollbar-width: thin;
                              scrollbar-color: #22c55e #1a1a1a;
                            }
                          </style>
                          ${guide.html_content}
                        `}
                        className="w-full h-full border-0"
                        sandbox="allow-same-origin allow-scripts"
                        title={`${guide.code_type} ${guide.code} Preview`}
                        style={{
                          backgroundColor: '#ffffff'
                        }}
                      />
                    </div>
                  )}

                  {/* Action */}
                  <CardContent className="p-4 pt-3">
                    <Link to={guideUrl}>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full border-matrix-green/30 text-matrix-green hover:bg-matrix-green/10"
                      >
                        Read Full Guide
                        <ExternalLink className="w-3 h-3 ml-2" />
                      </Button>
                    </Link>
                  </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
          </div>

          {/* Navigation */}
          {filteredGuides.length > 1 && (
            <div className="flex items-center justify-center gap-4 mb-6">
              <Button
                variant="outline"
                size="sm"
                onClick={scrollPrev}
                disabled={!canScrollPrev}
                className="border-matrix-green/30 text-matrix-green hover:bg-matrix-green/10 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>

              <div className="text-sm text-muted-foreground">
                Swipe or drag to navigate
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={scrollNext}
                disabled={!canScrollNext}
                className="border-matrix-green/30 text-matrix-green hover:bg-matrix-green/10 disabled:opacity-50"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </div>
        )}

        {/* View All Link */}
        <div className="text-center mt-8">
          <Link to="/cpt">
            <Button variant="outline" className="border-matrix-green/30 text-matrix-green hover:bg-matrix-green/10">
              View All Medical Codes
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}
